import axios from "axios";
import Jsona from "jsona";
import { useNotification } from "@/composables/useNotification";
import jsonApiClient from "../../lib/jsonapi_client";
import raftRestClient from "../../lib/raft_rest_client";
import { restClient } from "../../lib/raftApi";
import jsonApiFilterManager from "../../lib/JsonApiFilterManager";
import raftGenericHelper from "../../lib/raft_generic_helper";

const { showNotification } = useNotification();

const dataFormatter = new Jsona();

const _state = {
  spaces: [],
  labs: [],
  allLabs: [],
  facilities: [],
  selectedLabs: [],
  selectedFacilities: [],
  departments: [],
  selectedDepartments: [],
  campuses: [],
  controlAreas: [],
  selectedControlAreas: [],
  floors: [],
  selectedFloors: [],
  spaceHazards: [],
  selectedSpaceHazards: [],
  chemicalHazards: [],
  selectedCampuses: [],
  spacesQueryString: "",
  selectedBuildings: [],
  selectedSpaces: [],
  chemicalNameFilter: "",
  casNumberFilter: "",
  chemicalSynonymFilter: "",
  chemicalManufacturerFilter: "",
  chemicalProductFilter: "",
  benchFilter: "",
  shelfFilter: "",
  updateAfterDateFilter: "",
  updateBeforeDateFilter: "",
  containerExpiresAfterDateFilter: "",
  containerExpiresBeforeDateFilter: "",
  sdsDocumentDateAfterFilter: "",
  sdsDocumentDateBeforeFilter: "",
  sdsOriginFilter: "",
  specificLocationNoteFilter: "",
  containerIDsFilter: "",
  showDeletedContainers: false,
  showControlledSubstances: false,
  selectedDatabaseLinkage: [],
  selectedStorageGroup: [],
  selectedPhysicalStates: [],
  buildings: [],
  selectedHazardCodes: [],
  jsonApiFilters: {},
  selectedRegulations: [],
  labHazardMapping: {},
  spaceTitleDescription: "",
  spaceGroups: [],
  selectedSpaceGroups: [],
  notesFilter: "",
  spaceFilterDisabled: false,
  selectedMaxAllowedBSLOptions: [],
  chemicalOwningUserOptions: [],
  selectedChemicalOwningUsers: [],
  selectedExpiredStatus: "",
  selectedImportId: "",
};

const formatContainerIdsFromString = function (value) {
  return value
    .split(/[,\n]/)
    .map((splitValues) => splitValues.trim())
    .filter((trimmedValues) => trimmedValues !== "");
};

const updateContainerExpirationFilter = ({ afterDate, beforeDate }) => {
  jsonApiFilterManager.deleteFiltersByName([
    "container-expiration",
    "container-expires-after",
    "container-expires-before",
  ]);

  if (afterDate || beforeDate) {
    jsonApiFilterManager.addNotNullFilter(
      "container-expiration",
      "field_chemical_expiration",
    );
  }

  if (afterDate) {
    const afterFilterValue =
      raftGenericHelper.formatDateFilterYyyyMmDd(afterDate);
    jsonApiFilterManager.addFilter(
      "container-expires-after",
      "field_chemical_expiration",
      afterFilterValue,
      ">=",
      "",
      afterFilterValue,
    );
  }

  if (beforeDate) {
    const beforeFilterValue =
      raftGenericHelper.formatDateFilterYyyyMmDd(beforeDate);
    jsonApiFilterManager.addFilter(
      "container-expires-before",
      "field_chemical_expiration",
      beforeFilterValue,
      "<=",
      "",
      beforeFilterValue,
    );
  }
};

const state = {
  ..._state,
};

const getters = {
  allSpaces: (state) => state.spaces,
  allBuildings: (state) => state.buildings,
  allLabs: (state) => state.labs,
  getAllLabs: (state) => state.allLabs,
  allFacilities: (state) => state.facilities,
  allDepartments: (state) => state.departments,
  allCampuses: (state) => state.campuses,
  allFloors: (state) => state.floors,
  allControlAreas: (state) => state.controlAreas,
  allSpaceHazards: (state) => state.spaceHazards,
  nonJsonApiFilterKeys: (state) => {
    const isFilterKey = function (key) {
      return (
        (key.includes("Filter") ||
          key.includes("selected") ||
          key.includes("show")) &&
        !key.includes("jsonApiFilters") &&
        !key.includes("spaceFilterDisabled")
      );
    };
    const keys = Object.keys(state).filter((key) => isFilterKey(key));
    return keys;
  },
  filtersNotEmpty: (state, getters) => {
    const notEmpty = Object.entries(state).filter(([k, v]) => {
      const inKeys = getters.nonJsonApiFilterKeys.includes(k);
      const isDate = v && v instanceof Date;
      const stringNotEmpty = v && typeof v === "string" && v !== "";
      const arrayOrObjectNotEmpty =
        v &&
        typeof v === "object" &&
        (v.length > 0 ||
          (Object.entries(v).length > 0 && (v?.value || v?.key)));
      const booleanNotDefault = typeof v === "boolean" && v !== _state[k];
      return (
        inKeys &&
        (isDate || stringNotEmpty || arrayOrObjectNotEmpty || booleanNotDefault)
      );
    });
    return notEmpty;
  },
  filterCount: (state, getters) => getters.filtersNotEmpty.length,
  getSelectedSpaceHazards: (state) => state.selectedSpaceHazards,
  allChemicalHazards: (state) => state.chemicalHazards,
  getSelectedBuildingsFilter: (state) => state.selectedBuildings,
  getChemicalNameFilter: (state) =>
    decodeURIComponent(state.chemicalNameFilter),
  getCasNumberFilter: (state) => state.casNumberFilter,
  getChemicalSynonymFilter: (state) =>
    decodeURIComponent(state.chemicalSynonymFilter),
  getChemicalManufacturerFilter: (state) => state.chemicalManufacturerFilter,
  getChemicalProductFilter: (state) => state.chemicalProductFilter,
  getBenchFilter: (state) => state.benchFilter,
  getShelfFilter: (state) => state.shelfFilter,
  getSpecificLocationNoteFilter: (state) => state.specificLocationNoteFilter,
  getContainerIDsFilter: (state) => state.containerIDsFilter,
  getShowDeletedContainersFilter: (state) => state.showDeletedContainers,
  getSelectedHazardCodesFilter: (state) => state.selectedHazardCodes,
  getSelectedDatabaseLinkageFilter: (state) => state.selectedDatabaseLinkage,
  getSelectedStorageGroupFilter: (state) => state.selectedStorageGroup,
  getShowControlledSubstancesFilter: (state) => state.showControlledSubstances,
  getSelectedLabsFilter: (state) => state.selectedLabs,
  getSelectedSpacesFilter: (state) => state.selectedSpaces,
  getSelectedDepartmentsFilter: (state) => state.selectedDepartments,
  getSelectedCampusesFilter: (state) => state.selectedCampuses,
  getSelectedPhysicalStatesFilter: (state) => state.selectedPhysicalStates,
  getUpdatedAfterDateFilter: (state) => state.updateAfterDateFilter,
  getUpdatedBeforeDateFilter: (state) => state.updateBeforeDateFilter,
  getJsonApiFilters: (state) => state.jsonApiFilters,
  getContainerExpiresAfterFilter: (state) =>
    state.containerExpiresAfterDateFilter,
  getContainerExpiresBeforeFilter: (state) =>
    state.containerExpiresBeforeDateFilter,
  getSdsDocumentDateAfterFilter: (state) => state.sdsDocumentDateAfterFilter,
  getSdsDocumentDateBeforeFilter: (state) => state.sdsDocumentDateBeforeFilter,
  getSdsOriginFilter: (state) => state.sdsOriginFilter,
  getSelectedRegulations: (state) => state.selectedRegulations,
  getSelectedControlAreas: (state) => state.selectedControlAreas,
  getSelectedFloors: (state) => state.selectedFloors,
  getLabHazardMapping: (state) => state.labHazardMapping,
  getSpaceTitleDescription: (state) => state.spaceTitleDescription,
  allSpaceGroups: (state) => state.spaceGroups,
  getSelectedSpaceGroups: (state) => state.selectedSpaceGroups,
  getNotesFilter: (state) => state.notesFilter,
  getSpaceFilterDisabled: (state) => state.spaceFilterDisabled,
  getSelectedMaxAllowedBSLs: (state) => state.selectedMaxAllowedBSLOptions,
  getChemOwningUserOptions: (state) => state.chemicalOwningUserOptions,
  getSelectedChemOwningUsers: (state) => state.selectedChemicalOwningUsers,
  getSelectedExpiredStatusFilter: (state) => state.selectedExpiredStatus,
  getImportIdFilter: (state) => state.selectedImportId,
};

const mutations = {
  setSpaces: (state, spaces) => (state.spaces = spaces),
  appendSpaces: (state, spaces) =>
    (state.spaces = state.spaces?.concat(spaces)),
  setBuildings: (state, buildings) => (state.buildings = buildings),
  setCampuses: (state, campuses) => (state.campuses = campuses),
  setFloors: (state, floors) => (state.floors = floors),
  setControlAreas: (state, controlAreas) => (state.controlAreas = controlAreas),
  setDepartments: (state, departments) => (state.departments = departments),
  setLaboratories: (state, labs) => (state.labs = labs),
  setAllLaboratories: (state, labs) => (state.allLabs = labs),
  setFacilities: (state, facilities) => (state.facilities = facilities),
  setSpaceHazards: (state, hazards) => (state.spaceHazards = hazards),
  setSelectedSpaceHazards: (state, hazards) =>
    (state.selectedSpaceHazards = hazards),
  setChemicalHazards: (state, chemicalHazards) =>
    (state.chemicalHazards = chemicalHazards),
  setSpacesQueryString: (state, queryStr) =>
    (state.spacesQueryString = queryStr),
  setSelectedBuildings: (state, buildings) =>
    (state.selectedBuildings = buildings),
  setChemicalNameFilter: (state, chemicalName) =>
    (state.chemicalNameFilter = encodeURIComponent(chemicalName)),
  setCasNumberFilter: (state, casNumber) => (state.casNumberFilter = casNumber),
  setChemicalSynonymFilter: (state, chemSyn) =>
    (state.chemicalSynonymFilter = encodeURIComponent(chemSyn)),
  setChemicalManufacturerFilter: (state, manufacturer) =>
    (state.chemicalManufacturerFilter = manufacturer),
  setChemicalProductFilter: (state, product) =>
    (state.chemicalProductFilter = product),
  setBenchFilter: (state, bench) => (state.benchFilter = bench),
  setShelfFilter: (state, shelf) => (state.shelfFilter = shelf),
  setSpecificLocationNoteFilter: (state, specLocNote) =>
    (state.specificLocationNoteFilter = specLocNote),
  setContainerIDsFilter: (state, conIds) => (state.containerIDsFilter = conIds),
  setShowDeletedContainersFilter: (state, deletedCon) =>
    (state.showDeletedContainers = deletedCon),
  setSelectedHazardCodesFilter: (state, hazCodes) =>
    (state.selectedHazardCodes = hazCodes),
  setSelectedDatabaseLinkageFilter: (state, dbLink) =>
    (state.selectedDatabaseLinkage = dbLink),
  setSelectedExpiredStatusFilter: (state, expiredStatus) =>
    (state.selectedExpiredStatus = expiredStatus),
  setSelectedStorageGroupFilter: (state, dbLink) =>
    (state.selectedStorageGroup = dbLink),
  setShowControlledSubstancesFilter: (state, ctrlSub) =>
    (state.showControlledSubstances = ctrlSub),
  setSelectedLabsFilter: (state, selLabs) => (state.selectedLabs = selLabs),
  setSelectedSpacesFilter: (state, selSpaces) =>
    (state.selectedSpaces = selSpaces),
  setSelectedDepartmentsFilter: (state, selDept) =>
    (state.selectedDepartments = selDept),
  setSelectedCampusesFilter: (state, selCamp) =>
    (state.selectedCampuses = selCamp),
  setUpdatedAfterDateFilter: (state, aftDate) =>
    (state.updateAfterDateFilter = aftDate),
  setUpdatedBeforeDateFilter: (state, befDate) =>
    (state.updateBeforeDateFilter = befDate),
  setSelectedPhysicalStatesFilter: (state, phyState) =>
    (state.selectedPhysicalStates = phyState),
  setJsonApiFilters: (state, apiFilters) => (state.jsonApiFilters = apiFilters),
  setGroupContext: (state, value) => (state.getGroupContext = value),
  setContainerExpiresAfterFilter: (state, value) =>
    (state.containerExpiresAfterDateFilter = value),
  setContainerExpiresBeforeFilter: (state, value) =>
    (state.containerExpiresBeforeDateFilter = value),
  setSdsDocumentDateAfterFilter: (state, value) =>
    (state.sdsDocumentDateAfterFilter = value),
  setSdsDocumentDateBeforeFilter: (state, value) =>
    (state.sdsDocumentDateBeforeFilter = value),
  setSelectedRegulations: (state, value) => (state.selectedRegulations = value),
  setSelectedControlAreas: (state, value) =>
    (state.selectedControlAreas = value),
  setSelectedFloors: (state, value) => (state.selectedFloors = value),
  setLabHazardMapping: (state, value) => (state.labHazardMapping = value),
  setSpaceTitleDescription: (state, value) =>
    (state.spaceTitleDescription = value),
  setSdsOriginFilter: (state, value) => (state.sdsOriginFilter = value),
  setSpaceGroups: (state, value) => (state.spaceGroups = value),
  setSelectedSpaceGroups: (state, value) => (state.selectedSpaceGroups = value),
  setNotesFilter: (state, value) => (state.notesFilter = value),
  setSpaceFilterDisabled: (state, value) => (state.spaceFilterDisabled = value),
  setSelectedMaxAllowedBSLs: (state, value) =>
    (state.selectedMaxAllowedBSLOptions = value),
  setFilter: (state, payload) => {
    state[payload.filterKey] = payload.value;
  },
  setChemOwningUserOptions: (state, value) =>
    (state.chemicalOwningUserOptions = value),
  setImportIdFilter: (state, value) => (state.selectedImportId = value),
};

const actions = {
  clearAll({ state, getters, commit, dispatch }) {
    return getters.nonJsonApiFilterKeys.forEach((key) => {
      jsonApiFilterManager.deleteFiltersByName(key);
      return commit("setFilter", { filterKey: key, value: _state[key] });
    });
  },
  fetchSpaceList({ state, commit, rootState }) {
    jsonApiClient.getJSONapi(
      "node/space",
      state.spacesQueryString,
      function handleResponse(response) {
        commit("setSpaces", dataFormatter.deserialize(response));
        const totalPages = Math.ceil(response.meta.count / 50);
        let offset = 0;
        if (totalPages === 1) {
          return;
        }
        commit("setSpaces", []);
        for (let i = 0, len = totalPages; i < len; i++) {
          const query = state.spacesQueryString + "&page[offset]=" + offset;
          jsonApiClient.getJSONapi(
            "node/space",
            query,
            (innerResponse) => {
              commit("appendSpaces", dataFormatter.deserialize(innerResponse));
            },
            null,
            this,
          );

          offset += 50;
        }
      },
      null,
      this,
    );
  },
  fetchBuildingSpaceList({ state, commit, rootState }, buildingNid) {
    let groupID = rootState.config.groupContext;

    if (buildingNid && !groupID) {
      groupID = buildingNid;
    }

    raftRestClient.getRest(
      "/spaces_with_chemicals_in_building/" + groupID,
      {},
      (data) => {
        // handle success
        commit("setSpaces", data.chemical_spaces_in_building);
      },
      (error) => {
        // handle error
        showNotification(
          "error",
          "Error fetching spaces with chemicals in building:" + error,
        );
      },
    );
  },
  fetchGroupSpaceList({ state, commit, rootState }) {
    const groupID = rootState.config.groupContext;
    raftRestClient.getRest(
      "/spaces_with_chemicals_in_group/" + groupID,
      {},
      (data) => {
        // handle success
        commit("setSpaces", data.chemical_group_spaces);
      },
      (error) => {
        // handle error
        showNotification(
          "error",
          "Error fetching spaces with chemicals in group: " + error,
        );
      },
    );
  },
  fetchSpaceGroupsList({ state, commit, rootState }) {
    raftRestClient.getRest(
      "/space-groups",
      {},
      (data) => {
        const groups = [];
        const groupKeys = Object.values(data.groups);
        for (let i = 0; i < groupKeys.length; i++) {
          groups.push({ nid: data.groups[i].nid, label: data.groups[i].title });
        }
        commit("setSpaceGroups", groups);
      },
      (error) => {
        // handle error
        showNotification("error", "Error fetching space groups: " + error);
      },
    );
  },
  fetchBuildingList({ state, commit, rootState }) {
    return axios
      .get(rootState.config.restUrl + "/buildings" + "?_format=json", {
        headers: {
          "Content-Type": "application/vnd.api+json",
          Authorization: "Bearer " + rootState.config.jwt,
          crossDomain: true,
        },
      })
      .then((response) => {
        // handle success
        commit("setBuildings", response.data.buildings);
      })
      .catch((error) => {
        // handle error
        showNotification("error", "Error fetching buildings: " + error.message);
      });
  },
  fetchCampusList({ state, commit, rootState }) {
    return axios
      .get(rootState.config.restUrl + "/campuses" + "?_format=json", {
        headers: {
          "Content-Type": "application/vnd.api+json",
          Authorization: "Bearer " + rootState.config.jwt,
          crossDomain: true,
        },
      })
      .then((response) => {
        // handle success
        commit("setCampuses", response.data.campuses);
      })
      .catch((error) => {
        // handle error
        showNotification("error", "Error fetching campuses: " + error.message);
      });
  },
  fetchFloorsList({ state, commit, rootState }) {
    return axios
      .get(rootState.config.restUrl + "/floors" + "?_format=json", {
        headers: {
          "Content-Type": "application/vnd.api+json",
          Authorization: "Bearer " + rootState.config.jwt,
          crossDomain: true,
        },
      })
      .then((response) => {
        // handle success
        commit("setFloors", response.data.floors);
      })
      .catch((error) => {
        // handle error
        showNotification("error", "Error fetching floors: " + error.message);
      });
  },
  fetchControlAreasList({ state, commit, rootState }) {
    return axios
      .get(rootState.config.restUrl + "/control-areas" + "?_format=json", {
        headers: {
          "Content-Type": "application/vnd.api+json",
          Authorization: "Bearer " + rootState.config.jwt,
          crossDomain: true,
        },
      })
      .then((response) => {
        // handle success
        commit("setControlAreas", response.data.control_areas);
      })
      .catch((error) => {
        // handle error
        showNotification(
          "error",
          "Error fetching control areas: " + error.message,
        );
      });
  },
  fetchDepartmentList({ state, commit, rootState }) {
    return axios
      .get(rootState.config.restUrl + "/departments" + "?_format=json", {
        headers: {
          "Content-Type": "application/vnd.api+json",
          Authorization: "Bearer " + rootState.config.jwt,
          crossDomain: true,
        },
      })
      .then((response) => {
        // handle success
        commit("setDepartments", response.data.departments);
      })
      .catch((error) => {
        // handle error
        showNotification(
          "error",
          "Error fetching departments: " + error.message,
        );
      });
  },
  async fetchAllLabList({ state, commit, rootState }) {
    const options = {
      path: "/laboratories",
      queries: {},
    };
    try {
      const response = await restClient.get(options);
      commit("setAllLaboratories", response.laboratories);
    } catch (error) {
      showNotification(
        "error",
        "Error fetching all laboratories: " + error.message,
      );
    }
  },
  fetchBuildingLaboratoriesList({ state, commit, rootState }) {
    const groupID = rootState.config.groupContext;
    raftRestClient.getRest(
      "/chemical_owning_groups_in_building/" + groupID,
      {},
      (data) => {
        // handle success
        commit("setLaboratories", data.chemical_groups_in_building);
      },
      (error) => {
        // handle error
        showNotification(
          "error",
          "Error fetching chemical owning groups in building: " + error.message,
        );
      },
    );
  },
  fetchSpaceLaboratoriesList({ state, commit, rootState }) {
    const groupID = rootState.config.groupContext;
    raftRestClient.getRest(
      "/chemical_groups_in_space/" + groupID,
      {},
      (data) => {
        // handle success
        commit("setLaboratories", data.chemical_groups_in_space);
      },
      (error) => {
        // handle error
        showNotification(
          "error",
          "Error fetching chemical groups in space: " + error.message,
        );
      },
    );
  },
  fetchLaboratoriesList({ state, commit, rootState }) {
    return axios
      .get(
        rootState.config.restUrl + "/chemical_owning_groups" + "?_format=json",
        {
          headers: {
            "Content-Type": "application/vnd.api+json",
            Authorization: "Bearer " + rootState.config.jwt,
            crossDomain: true,
          },
        },
      )
      .then((response) => {
        // handle success
        commit("setLaboratories", response.data.chemical_owning_groups);
      })
      .catch((error) => {
        // handle error
        showNotification(
          "error",
          "Error fetching chemical owning groups: " + error.message,
        );
      });
  },
  fetchFacilitiesList({ state, commit, rootState }) {
    return axios
      .get(rootState.config.restUrl + "/facilities" + "?_format=json", {
        headers: {
          "Content-Type": "application/vnd.api+json",
          Authorization: "Bearer " + rootState.config.jwt,
          crossDomain: true,
        },
      })
      .then((response) => {
        // handle success
        commit("setFacilities", response.data.facilities);
      })
      .catch((error) => {
        // handle error
        showNotification(
          "error",
          "Error fetching facilities: " + error.message,
        );
      });
  },
  fetchLabHazardMapping({ state, commit, rootState }) {
    return axios
      .get(rootState.config.restUrl + "/lab_hazard_mapping" + "?_format=json", {
        headers: {
          "Content-Type": "application/vnd.api+json",
          Authorization: "Bearer " + rootState.config.jwt,
          crossDomain: true,
        },
      })
      .then((response) => {
        // handle success
        commit("setLabHazardMapping", response.data.lab_hazard_mapping);
      })
      .catch((error) => {
        // handle error
        showNotification(
          "error",
          "Error fetching lab hazard mapping: " + error.message,
        );
      });
  },
  fetchSpaceHazardsList({ state, commit, rootState }) {
    axios
      .get(
        rootState.config.restUrl +
          "/config_terms?vocab=lab_hazards&_format=json",
        {
          headers: {
            "Content-Type": "application/vnd.api+json",
            Authorization: "Bearer " + rootState.config.jwt,
            crossDomain: true,
          },
        },
      )
      .then((response) => {
        // handle success
        const terms = response.data.config_terms;
        const termMap = {};
        for (let i = 0; i < terms.length; i++) {
          terms[i].child_terms = Object.values(
            response.data.config_term_children[terms[i].id],
          );
          termMap[terms[i].id] = terms[i];
          let noMoreParents = false;
          let parentCount = 0;
          if (terms[i].parents[0] !== "0") {
            parentCount += 1;
            let parentTerm = termMap[terms[i].parents[0]];
            while (!noMoreParents) {
              if (parentTerm.parents[0] !== "0") {
                parentCount += 1;
                parentTerm = termMap[parentTerm.parents[0]];
              } else {
                noMoreParents = true;
              }
            }
          }
          let labelPrefix = "";
          for (let j = 0; j < parentCount; j++) {
            labelPrefix = "-" + labelPrefix;
          }
          terms[i].label = labelPrefix + terms[i].label;
        }
        if (Array.isArray(terms)) {
          commit("setSpaceHazards", terms);
        } else {
          commit("setSpaceHazards", []);
        }
      })
      .catch((error) => {
        // handle error
        showNotification(
          "error",
          "Error fetching space hazards: " + error.message,
        );
      });
  },
  fetchChemicalHazardsList({ state, commit, rootState }) {
    axios
      .get(rootState.config.restUrl + "/chemical-hazards" + "?_format=json", {
        headers: {
          "Content-Type": "application/vnd.api+json",
          Authorization: "Bearer " + rootState.config.jwt,
          crossDomain: true,
        },
      })
      .then((response) => {
        const hazards = [];
        Object.keys(response.data.chemical_hazards).forEach((hazard_key) => {
          hazards.push({
            key: hazard_key,
            label: response.data.chemical_hazards[hazard_key],
          });
        });
        // handle success
        commit("setChemicalHazards", hazards);
      })
      .catch((error) => {
        // handle error
        showNotification(
          "error",
          "Error fetching chemical hazards: " + error.message,
        );
      });
  },

  /**
   * The context object passed to all actions in a Vuex store.
   *
   * @typedef {Object} VuexContextObject
   * @property {Object} state
   * @property {Object} rootState
   * @property {Object} commit
   * @property {Object} dispatch
   * @property {Object} getters
   */

  /**
   * One choice made from a filter.
   *
   * @typedef {Object} FilterChoiceItem
   * @property {string} [uuid] - Unique id representing the entity
   * @property {string} [title] - Name of the entity
   * @property {string} nid - Drupal internal node id for the entity
   */

  /**
   * A list of choices made from a filter, zero or more.
   *
   * @typedef {FilterChoiceItem[]} FilterChoices - one or more choices made from a filter input
   */

  /**
   * The payload emitted by filters for the update-filter event.
   *
   * @typedef {Object} UpdateFilterEventPayload
   * @property {FilterChoices} itemsSelected - an array of 0 or more objects
   * @property {string} id
   * @property {string} path - the JSON:API path for this filter
   * @property {any} value - the selected value of the filter
   * @property {string} filterAction - the Vuex action to call when setting this filter
   * @property {string} filterKey - the object property to pass to the Vuex action
   * @property {string} trackBy - the object property to use when tracking the selected value
   */

  /**
   * updateSelectedBuildings
   * @param {VuexContextObject} context - The context object passed to all actions in a Vuex store.
   * @param {UpdateFilterEventPayload|FilterChoices} payload - Zero or more filter choices.
   */
  updateSelectedBuildings({ getters, commit, dispatch }, payload) {
    // eslint-disable-next-line valid-jsdoc
    /**
     * isEventPayload - Checks if value is an event payload
     *
     * @param {UpdateFilterEventPayload|FilterChoices} value - Value to test
     * @return {value is UpdateFilterEventPayload}
     */
    function isEventPayload(value) {
      return value?.hasOwnProperty("itemsSelected");
    }

    if (isEventPayload(payload)) {
      payload = payload.itemsSelected;
    }

    const selectedBuildings = payload;

    commit("setSelectedBuildings", selectedBuildings);
    if (!selectedBuildings || selectedBuildings?.length <= 0) {
      dispatch("clearListedSpaces");
      dispatch("updateSpaceFilterDisabled", true);
      jsonApiFilterManager.deleteFiltersByName([
        "selectedBuildings",
        "selectedSpaces",
      ]);
      return;
    }

    const spaceQueries = {
      "fields[node--space]":
        "drupal_internal__nid,title,field_parent_location.nid",
      sort: "title",
      "page[limit]": "50",
      "filter[building][condition][path]": "field_parent_location.nid",
      "filter[building][condition][operator]": "IN",
    };
    let querystring = Object.keys(spaceQueries)
      .map((key) => key + "=" + spaceQueries[key])
      .join("&");

    if (Array.isArray(selectedBuildings) && selectedBuildings?.[0]?.nid) {
      querystring = selectedBuildings?.reduce(
        (amendedString, building) =>
          `${amendedString}&filter[building][condition][value][]=${building.nid}`,
        querystring,
      );
    }

    dispatch("updateSpacesQueryString", querystring);
    dispatch("fetchSpaceList");
    dispatch("updateSpaceFilterDisabled", false);

    let options = [];
    if (Array.isArray(selectedBuildings) && selectedBuildings?.[0]?.nid) {
      options = selectedBuildings.reduce(
        (optionSet, building) => [...optionSet, building?.nid],
        [],
      );
    }
    jsonApiFilterManager.deleteFiltersByName(["selectedBuildings"]);
    if (options.length > 0) {
      jsonApiFilterManager.addMultivalueNamedFilter(
        "selectedBuildings",
        "field_chemical_space.field_parent_location.drupal_internal__nid",
        options,
        "",
        selectedBuildings,
      );
    }
  },
  updateChemicalNameFilter({ commit }, value) {
    commit("setChemicalNameFilter", value);
  },
  updateCasNumberFilter({ commit }, value) {
    commit("setCasNumberFilter", value);
  },
  updateChemicalSynonymFilter({ commit }, payload) {
    let value = payload ?? "";
    if (
      payload &&
      typeof payload === "object" &&
      Object.hasOwn(payload, "value")
    ) {
      value = payload.value ?? "";
    }
    if (!value) {
      jsonApiFilterManager.deleteFiltersByName([payload.filterKey]);
      return;
    }
    jsonApiFilterManager.addFilter(
      payload.filterKey,
      payload.path,
      value,
      payload.operator,
      "",
      value,
    );
    commit("setChemicalSynonymFilter", value);
  },
  updateChemicalManufacturerFilter({ commit }, value) {
    commit("setChemicalManufacturerFilter", value);
  },
  updateChemicalProductFilter({ commit }, value) {
    commit("setChemicalProductFilter", value);
  },
  updateBenchFilter({ commit }, value) {
    commit("setBenchFilter", value);
  },
  updateShelfFilter({ commit }, value) {
    commit("setShelfFilter", value);
  },
  updateSpecificLocationNoteFilter({ commit }, value) {
    commit("setSpecificLocationNoteFilter", value);
  },
  updateImportIdFilter({ commit }, payload) {
    const value = payload?.value || "";
    commit("setImportIdFilter", payload);
    jsonApiFilterManager.deleteFiltersByName(["importIdFilter"]);
    // Import ID value is only a single value for now
    if (payload?.value) {
      if (payload?.selected) {
        jsonApiFilterManager.addMultivalueStartsWithFilter(
          "importIdFilter",
          "field_chemical_import_id",
          [value],
          "",
          "",
        );
      } else {
        jsonApiFilterManager.addMultivalueNamedFilter(
          "importIdFilter",
          "field_chemical_import_id",
          [value],
          "",
          "",
        );
      }
    }
  },
  updateContainerIDsFilter({ state, getters, commit }, payload) {
    let value = payload ?? "";
    if (
      payload &&
      typeof payload === "object" &&
      Object.hasOwn(payload, "value")
    ) {
      value = payload.value ?? "";
    }
    commit("setContainerIDsFilter", payload);
    const newContainers = formatContainerIdsFromString(value);
    jsonApiFilterManager.deleteFiltersByName(["containerIDsFilter"]);
    const options = [];
    for (let i = 0, len = newContainers.length; i < len; i++) {
      options.push(newContainers[i]);
    }
    if (options.length > 0) {
      let startsWith = payload.selected;
      if (payload.selected === "false") {
        startsWith = false;
      }
      if (payload.selected === "true") {
        startsWith = true;
      }
      if (startsWith) {
        jsonApiFilterManager.addMultivalueStartsWithFilter(
          "containerIDsFilter",
          "field_chemical_container_id",
          options,
          "",
          newContainers,
        );
      } else {
        jsonApiFilterManager.addMultivalueNamedFilter(
          "containerIDsFilter",
          "field_chemical_container_id",
          options,
          "",
          newContainers,
        );
      }
    }
  },
  updateShowDeletedContainersFilter({ commit }, payload) {
    const value = payload.value ?? "";
    const showDeleted = value === true || value === "true";
    jsonApiFilterManager.deleteFiltersByName(["containerExpires"]);
    if (showDeleted === true) {
      jsonApiFilterManager.addFilter(
        "containerExpires",
        "operative_status",
        "0",
        "=",
        "",
        "0",
      );
    } else {
      jsonApiFilterManager.addFilter(
        "containerExpires",
        "operative_status",
        "1",
        "=",
        "",
        "1",
      );
    }
    jsonApiFilterManager.deleteFiltersByName(["operativeStatus"]);
    if (showDeleted === true) {
      jsonApiFilterManager.addFilter(
        "operativeStatus",
        "operative_status",
        "0",
        "=",
        "",
        "0",
      );
    } else {
      jsonApiFilterManager.addFilter(
        "operativeStatus",
        "operative_status",
        "1",
        "=",
        "",
        "1",
      );
    }
    commit("setShowDeletedContainersFilter", showDeleted);
  },
  updateSelectedHazardCodesFilter({ commit }, value) {
    commit("setSelectedHazardCodesFilter", value);
  },
  updateSelectedExpiredStatusFilter({ commit, dispatch }, payload) {
    let value = payload ?? "";
    if (
      payload &&
      typeof payload === "object" &&
      Object.hasOwn(payload, "value")
    ) {
      value = payload.value ?? "";
    }
    const key = value?.key;

    const now = new Date();
    const formattedDate = raftGenericHelper.formatDateFilterYyyyMmDd(now);

    // If Expired status filter is cleared, clear Container Expiration Dates
    // If Option: Any or Date Blank, clear clear Container Expiration Dates too
    if (!key || key === "any" || key === "date_blank") {
      dispatch("updateContainerExpiresBeforeFilter", "");
      dispatch("updateContainerExpiresAfterFilter", "");
    }

    // Option: Expired, expiration dates before today and today
    if (key === "expired") {
      // Update query to BEFORE expiration date with today's date
      dispatch("updateContainerExpiresBeforeFilter", { value: formattedDate });
      dispatch("updateContainerExpiresAfterFilter", "");
    }
    // Option: Not Expired, expiration dates after today
    if (key === "not_expired") {
      // Update query to AFTER expiration date with today's date
      dispatch("updateContainerExpiresAfterFilter", { value: formattedDate });
      dispatch("updateContainerExpiresBeforeFilter", "");
    }
    // Option: Date Blank
    if (key === "date_blank") {
      // Update query to expiration date where IS NULL
      jsonApiFilterManager.addNullFilter(
        "container-expiration",
        "field_chemical_expiration",
      );
    }
    commit("setSelectedExpiredStatusFilter", value);
  },
  updateSelectedDatabaseLinkageFilter({ commit }, payload) {
    let value = payload ?? "";
    if (
      payload &&
      typeof payload === "object" &&
      Object.hasOwn(payload, "value")
    ) {
      value = payload.value ?? "";
    }
    jsonApiFilterManager.deleteFiltersByName(["db-link"]);
    if (value.key === 1 || value.key === "1") {
      jsonApiFilterManager.addFilter(
        "db-link",
        "field_chemical_type.field_chemdb_gdn",
        "0",
        ">",
        "",
        "0",
      );
    }
    if (value.key === 2 || value.key === "2") {
      jsonApiFilterManager.addFilter(
        "db-link",
        "field_chemical_type.field_chemdb_gdn",
        "0",
        "=",
        "",
        "0",
      );
    }
    commit("setSelectedDatabaseLinkageFilter", value);
  },
  updateSelectedStorageGroupFilter({ commit }, value) {
    commit("setSelectedStorageGroupFilter", value);
  },
  updateShowControlledSubstancesFilter({ commit }, payload) {
    const value = payload.value ?? "";
    const showControlledSubstances = value === true || value === "true";
    jsonApiFilterManager.deleteFiltersByName(["controlled-sub"]);
    if (showControlledSubstances) {
      jsonApiFilterManager.addFilter(
        "controlled-sub",
        "field_chemical_dea_controlled",
        "1",
        "=",
        "",
        "1",
      );
    }
    commit("setShowControlledSubstancesFilter", showControlledSubstances);
  },
  updateSelectedLabsFilter({ commit }, value) {
    commit("setSelectedLabsFilter", value);
  },
  updateSelectedDepartmentsFilter({ commit }, value) {
    commit("setSelectedDepartmentsFilter", value);
  },
  updateSelectedCampusesFilter({ commit }, value) {
    commit("setSelectedCampusesFilter", value);
  },
  updateSelectedSpacesFilter({ commit }, value) {
    commit("setSelectedSpacesFilter", value);
  },
  updateBeforeAndAfter(
    { state, getters },
    { beforeGetter, afterGetter, filterKey },
  ) {
    const afterDate =
      getters[afterGetter] !== "" ? new Date(getters[afterGetter]) : "";
    const beforeDate =
      getters[beforeGetter] !== "" ? new Date(getters[beforeGetter]) : "";
    jsonApiFilterManager.deleteFiltersByName([
      `${filterKey}-after`,
      `${filterKey}-before`,
    ]);

    if (afterDate) {
      const afterDateTime = raftGenericHelper.datepickerValueToUnixTimestamp(
        afterDate,
        true,
        0,
        0,
        0,
      );
      jsonApiFilterManager.addFilter(
        `${filterKey}-after`,
        "changed",
        afterDateTime,
        ">=",
        "",
        afterDateTime,
      );
    }
    if (beforeDate) {
      const beforeDateTime = raftGenericHelper.datepickerValueToUnixTimestamp(
        beforeDate,
        true,
        23,
        59,
        59,
      );
      jsonApiFilterManager.addFilter(
        `${filterKey}-before`,
        "changed",
        beforeDateTime,
        "<=",
        "",
        beforeDateTime,
      );
    }
  },
  updateUpdatedAfterDateFilter({ state, getters, commit, dispatch }, payload) {
    let value = payload ?? "";
    if (
      payload &&
      typeof payload === "object" &&
      Object.hasOwn(payload, "value")
    ) {
      value = payload.value ?? "";
    }
    commit("setUpdatedAfterDateFilter", value);
    dispatch("updateBeforeAndAfter", {
      beforeGetter: "getUpdatedBeforeDateFilter",
      afterGetter: "getUpdatedAfterDateFilter",
      filterKey: "date",
    });
  },
  updateUpdatedBeforeDateFilter({ state, getters, commit, dispatch }, payload) {
    let value = payload ?? "";
    if (
      payload &&
      typeof payload === "object" &&
      Object.hasOwn(payload, "value")
    ) {
      value = payload.value ?? "";
    }
    commit("setUpdatedBeforeDateFilter", value);
    dispatch("updateBeforeAndAfter", {
      beforeGetter: "getUpdatedBeforeDateFilter",
      afterGetter: "getUpdatedAfterDateFilter",
      filterKey: "date",
    });
  },
  updateSpacesQueryString({ commit }, value) {
    commit("setSpacesQueryString", value);
  },
  updateSpaceFilterDisabled({ commit }, value) {
    commit("setSpaceFilterDisabled", value);
  },
  clearListedSpaces({ commit }) {
    commit("setSpaces", []);
    commit("setSelectedSpacesFilter", []);
  },
  resetSelectedLabs({ commit }, value) {
    commit("setSelectedLabsFilter", []);
  },
  updateSelectedPhysicalStatesFilter({ commit }, value) {
    commit("setSelectedPhysicalStatesFilter", value);
  },
  updateContainerExpiresAfterFilter({ state, getters, commit }, payload) {
    let value = payload ?? "";
    if (
      payload &&
      typeof payload === "object" &&
      Object.hasOwn(payload, "value")
    ) {
      value = payload.value ?? "";
    }
    commit("setContainerExpiresAfterFilter", value);
    const afterDate = getters.getContainerExpiresAfterFilter;
    const beforeDate = getters.getContainerExpiresBeforeFilter;
    updateContainerExpirationFilter({ afterDate, beforeDate });

    const selectedExpiredStatus = getters.getSelectedExpiredStatusFilter;
    if (!afterDate && !beforeDate && selectedExpiredStatus) {
      commit("setSelectedExpiredStatusFilter", "");
    }
  },
  updateContainerExpiresBeforeFilter({ state, getters, commit }, payload) {
    let value = payload ?? "";
    if (
      payload &&
      typeof payload === "object" &&
      Object.hasOwn(payload, "value")
    ) {
      value = payload.value ?? "";
    }
    commit("setContainerExpiresBeforeFilter", value);
    const afterDate = getters.getContainerExpiresAfterFilter;
    const beforeDate = getters.getContainerExpiresBeforeFilter;
    updateContainerExpirationFilter({ afterDate, beforeDate });

    const selectedExpiredStatus = getters.getSelectedExpiredStatusFilter;
    if (!afterDate && !beforeDate && selectedExpiredStatus) {
      commit("setSelectedExpiredStatusFilter", "");
    }
  },
  updateSdsDocumentDateAfterFilter({ state, getters, commit }, payload) {
    let value = payload ?? "";
    if (
      payload &&
      typeof payload === "object" &&
      Object.hasOwn(payload, "value")
    ) {
      value = payload.value ?? "";
    }
    commit("setSdsDocumentDateAfterFilter", value);
    const afterDate = getters.getSdsDocumentDateAfterFilter;
    const beforeDate = getters.getSdsDocumentDateBeforeFilter;
    jsonApiFilterManager.deleteFiltersByName([
      "sds-document-date",
      "sds-document-date-after",
      "sds-document-date-before",
    ]);

    if (afterDate || beforeDate) {
      jsonApiFilterManager.addNotNullFilter(
        "sds-document-date",
        "field_chemical_sds.field_sds_document_date",
      );
    }

    if (afterDate) {
      const afterFilterValue =
        raftGenericHelper.datepickerValueToIso(afterDate);
      jsonApiFilterManager.addFilter(
        "sds-document-date-after",
        "field_chemical_sds.field_sds_document_date",
        afterFilterValue,
        ">=",
        "",
        afterFilterValue,
      );
    }

    if (beforeDate) {
      const beforeFilterValue =
        raftGenericHelper.datepickerValueToIso(beforeDate);
      jsonApiFilterManager.addFilter(
        "sds-document-date-before",
        "field_chemical_sds.field_sds_document_date",
        beforeFilterValue,
        "<=",
        "",
        beforeFilterValue,
      );
    }
  },
  updateSdsDocumentDateBeforeFilter({ state, getters, commit }, payload) {
    let value = payload ?? "";
    if (
      payload &&
      typeof payload === "object" &&
      Object.hasOwn(payload, "value")
    ) {
      value = payload.value ?? "";
    }
    commit("setSdsDocumentDateBeforeFilter", value);
    const afterDate = getters.getSdsDocumentDateAfterFilter;
    const beforeDate = getters.getSdsDocumentDateBeforeFilter;
    jsonApiFilterManager.deleteFiltersByName([
      "sds-document-date",
      "sds-document-date-after",
      "sds-document-date-before",
    ]);

    if (afterDate || beforeDate) {
      jsonApiFilterManager.addNotNullFilter(
        "sds-document-date",
        "field_chemical_sds.field_sds_document_date",
      );
    }

    if (afterDate) {
      const afterFilterValue =
        raftGenericHelper.datepickerValueToIso(afterDate);
      jsonApiFilterManager.addFilter(
        "sds-document-date-after",
        "field_chemical_sds.field_sds_document_date",
        afterFilterValue,
        ">=",
        "",
        afterFilterValue,
      );
    }

    if (beforeDate) {
      const beforeFilterValue =
        raftGenericHelper.datepickerValueToIso(beforeDate);
      jsonApiFilterManager.addFilter(
        "sds-document-date-before",
        "field_chemical_sds.field_sds_document_date",
        beforeFilterValue,
        "<=",
        "",
        beforeFilterValue,
      );
    }
  },
  updateNotesFilter({ commit }, value) {
    commit("setNotesFilter", value);
  },
  updateSdsOriginFilter({ commit }, $e) {
    commit("setFilter", { filterKey: $e.filterKey, value: $e.value });
    let sdsOrigin = $e.value;
    sdsOrigin = sdsOrigin?.key;
    jsonApiFilterManager.deleteFiltersByName([
      "sds-origin-type-filter",
      "sds-origin-current-filter",
      "sds-origin-null-filter",
    ]);
    if (!sdsOrigin) {
      return;
    }
    const statusTypes = ["sds_external", "sds_url", "sds_file"];
    if (statusTypes.includes(sdsOrigin)) {
      jsonApiFilterManager.addFilter(
        "sds-origin-type-filter",
        "field_chemical_sds.type.meta.drupal_internal__target_id",
        sdsOrigin,
        "=",
        "",
        sdsOrigin,
      );
    }
    if (sdsOrigin === "outdated") {
      jsonApiFilterManager.addFilter(
        "sds-origin-current-filter",
        "field_chemical_sds.field_current",
        "0",
        "=",
        "",
        "raft_metadata--sds_external",
      );
    }
    if (sdsOrigin === "no_attachment") {
      jsonApiFilterManager.addNullFilter(
        "sds-origin-null-filter",
        "field_chemical_sds",
      );
    }
  },
  updateFilter(
    { commit },
    { filterKey, value, operator, path, useJsonApiFilterManager },
  ) {
    commit("setFilter", { filterKey, value });
    if (useJsonApiFilterManager) {
      if (!value) {
        return jsonApiFilterManager.deleteFiltersByName([filterKey]);
      }
      return jsonApiFilterManager.addFilter(filterKey, path, value, operator);
    }
  },
  updateMultivalueFilter(
    { commit },
    { filterKey, value, operator, path, useJsonApiFilterManager, trackBy },
  ) {
    commit("setFilter", { filterKey, value });
    if (useJsonApiFilterManager) {
      if (!value || value?.length === 0) {
        return jsonApiFilterManager.deleteFiltersByName([filterKey]);
      }
      return jsonApiFilterManager.addMultivalueNamedFilter(
        filterKey,
        path,
        value.map((v) => v[trackBy]),
        operator,
      );
    }
  },
  fetchChemicalOwningUsers({ state, commit, rootState }) {
    const queries = {};
    if (rootState.config.groupContext) {
      queries["rog-type"] = "node";
      queries["rog-id"] = rootState.config.groupContext;
    }
    raftRestClient.getRest("/chemical-owning-users", queries, (data) => {
      commit("setChemOwningUserOptions", data.users);
    });
  },
};

export default {
  state,
  getters,
  mutations,
  actions,
};
